/**
 * @lines98/client/services/oauth-login — OAuth init flow (T-6 client).
 *
 * Per ADR-0006 §D1 Pipeline Stage 3 + §D7 (callback flow):
 *   1. Click «Войти через <provider>» → loginWith<Provider>().
 *   2. Ensure identity exists (boot if not — guests can't init OAuth without
 *      a Bearer token).
 *   3. POST /auth/oauth/<provider>/init с current Bearer → { authorizationUrl, state }.
 *   4. Save state в sessionStorage (will be read on callback page для validation).
 *   5. `window.location.href = authorizationUrl` — redirect к provider consent screen.
 *
 * State validation:
 *   - The state HMAC is verified server-side too (oauth-state.verifyState с
 *     LINES98_AUTH_SECRET). Client-side validation is a defense-in-depth check
 *     that the callback returned matches what we sent (CSRF / tab-mixup
 *     protection).
 *
 * Cross-references:
 *   - ADR-0006 §D1 (Pipeline Stage 3 — OAuth init flow).
 *   - ADR-0006 §D7 (callback flow on server).
 *   - services/identity.ts (Bearer source + bootstrap).
 *   - services/api-client.ts (fetch wrapper).
 *   - screens/oauth-callback-screen.ts (callback page reader).
 */

import { apiClient } from './api-client.js';
import { bootIdentity, getCurrent } from './identity.js';

// =============================================================================
// Types
// =============================================================================

export type OAuthProvider = 'google' | 'discord';

/**
 * Server response shape for POST /auth/oauth/<provider>/init. Aligned with
 * packages/server/src/routes/auth/oauth/shared.ts:initSuccessResponseSchema.
 */
interface OAuthInitResponse {
  authorizationUrl: string;
  state: string;
}

export class OAuthLoginError extends Error {
  readonly kind: 'no_identity' | 'init_failed' | 'network';
  readonly status?: number;
  constructor(
    kind: 'no_identity' | 'init_failed' | 'network',
    message: string,
    status?: number,
  ) {
    super(message);
    this.kind = kind;
    this.status = status;
    this.name = 'OAuthLoginError';
  }
}

// =============================================================================
// SessionStorage keys
// =============================================================================

export const OAUTH_STATE_STORAGE_KEY = 'lines98.oauth.state.v1';
export const OAUTH_PROVIDER_STORAGE_KEY = 'lines98.oauth.provider.v1';

// =============================================================================
// Public API
// =============================================================================

/**
 * Initiate Google OAuth login. Redirects browser к Google consent screen.
 * Returns ONLY if the init request failed (caller should display error);
 * на success we navigate away and never return.
 */
export async function loginWithGoogle(): Promise<void> {
  await loginWith('google');
}

/**
 * Initiate Discord OAuth login. Redirects browser к Discord consent screen.
 * Returns ONLY if the init request failed.
 */
export async function loginWithDiscord(): Promise<void> {
  await loginWith('discord');
}

async function loginWith(provider: OAuthProvider): Promise<void> {
  // Step 1: ensure identity exists. If user clicked login before boot completed
  // (rare race), bootIdentity blocks; if mint already happened, returns existing.
  let identity = getCurrent();
  if (!identity) {
    identity = await bootIdentity();
  }
  if (!identity) {
    throw new OAuthLoginError(
      'no_identity',
      'Identity mint failed — cannot initiate OAuth login. Check network connection.',
    );
  }

  // Step 2: POST /init (Bearer attached автоматически by api-client).
  const response = await apiClient.post<OAuthInitResponse>(
    `/auth/oauth/${provider}/init`,
    {},
  );
  if (!response.ok) {
    throw new OAuthLoginError(
      'init_failed',
      `OAuth ${provider} init failed: HTTP ${response.status}`,
      response.status,
    );
  }

  // Step 3: save state + provider for callback page CSRF validation.
  try {
    window.sessionStorage.setItem(OAUTH_STATE_STORAGE_KEY, response.body.state);
    window.sessionStorage.setItem(OAUTH_PROVIDER_STORAGE_KEY, provider);
  } catch {
    // sessionStorage disabled — proceed without it; server-side HMAC check
    // is the authoritative defense. Client-side check just won't fire.
  }

  // Step 4: redirect к provider. After this line, page navigates away;
  // function does NOT return on the happy path.
  window.location.href = response.body.authorizationUrl;
}

/**
 * Read the expected state value from sessionStorage (called by callback screen).
 * Returns null if not present.
 */
export function readExpectedState(): string | null {
  try {
    return window.sessionStorage.getItem(OAUTH_STATE_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Read the expected provider from sessionStorage (called by callback screen).
 * Returns null if not present.
 */
export function readExpectedProvider(): OAuthProvider | null {
  try {
    const raw = window.sessionStorage.getItem(OAUTH_PROVIDER_STORAGE_KEY);
    if (raw === 'google' || raw === 'discord') return raw;
    return null;
  } catch {
    return null;
  }
}

/** Clear OAuth init state from sessionStorage. Called after callback completes. */
export function clearOAuthState(): void {
  try {
    window.sessionStorage.removeItem(OAUTH_STATE_STORAGE_KEY);
    window.sessionStorage.removeItem(OAUTH_PROVIDER_STORAGE_KEY);
  } catch {
    // ignore
  }
}
